// Integração real com o GPS Drive Backend (src/vehicle-metadata) — cadastro
// do veículo (marca/modelo/ano/rastreador/instalação/imagens). Nunca fala
// com o Traccar — ver src/services/vehicles.service.ts para isso.
import { gpsDriveDelete, gpsDriveGet, gpsDrivePatch, gpsDrivePut, gpsDriveUpload } from "@/lib/api/gpsdrive-client";
import { compressImageFile } from "@/lib/compress-image";
import type {
  UpdateVehicleMetadataInput,
  VehicleImageEntry,
  VehicleMetadata,
  VehicleMetadataSummaryEntry,
  VehicleResellerCompanyEntry,
} from "@/types/vehicle-metadata";

// Índice de busca (2026-10-01) — GET /api/v1/vehicles/search-index, só
// equipe (admin/funcionário). Chave = id do device no Traccar.
export interface VehicleSearchClientRef {
  name: string;
  email: string;
  cpfCnpj: string | null;
  companyName: string | null;
}
export interface VehicleSearchEntry {
  clients: VehicleSearchClientRef[];
  simNumber?: string;
  simIccid?: string;
  chassis?: string;
}

// Bug 2026-10-01 (colunas Simcard/Fornecedor/Modelo vazias na tela
// Veículos): a frota inteira (~1.600 ids) ia numa URL só e o Nginx
// recusava com 414 (URI muito longa). Divide em lotes de 200 ids e junta.
const BATCH_SIZE = 200;
async function getInBatches<T>(path: string, ids: (string | number)[]): Promise<T[]> {
  const batches: (string | number)[][] = [];
  for (let i = 0; i < ids.length; i += BATCH_SIZE) batches.push(ids.slice(i, i + BATCH_SIZE));
  const results = await Promise.all(batches.map((b) => gpsDriveGet<T[]>(`${path}?deviceIds=${b.join(",")}`)));
  return results.flat();
}

export const vehicleMetadataService = {
  getSearchIndex: () => gpsDriveGet<Record<string, VehicleSearchEntry>>("/api/v1/vehicles/search-index"),
  // Renomear pelo GPS Drive Backend (pedido 2026-09-24) — único jeito do
  // cliente, que é deviceReadonly no Traccar (PUT /api/devices direto dá
  // 400 pra ele). Exige devices.edit e device vinculado ao usuário.
  renameDevice: (deviceId: string | number, name: string) =>
    gpsDrivePatch<unknown>(`/api/v1/vehicles/${deviceId}/name`, { name }),
  get: (traccarDeviceId: string | number) =>
    gpsDriveGet<VehicleMetadata>(`/api/v1/vehicle-metadata/${traccarDeviceId}`),
  // Busca as fotos de vários veículos numa única chamada (a listagem da
  // frota usa isso, nunca um GET por veículo — ver GET /images no backend).
  getImages: (traccarDeviceIds: (string | number)[]) =>
    getInBatches<VehicleImageEntry>("/api/v1/vehicle-metadata/images", traccarDeviceIds),
  // Busca fornecedor/operadora/número do SIM/modelo do rastreador de vários
  // veículos numa única chamada (mesma ideia de getImages, ver GET /summary
  // no backend) — usada pela listagem da frota (rota /vehicles).
  getSummaries: (traccarDeviceIds: (string | number)[]) =>
    getInBatches<VehicleMetadataSummaryEntry>("/api/v1/vehicle-metadata/summary", traccarDeviceIds),
  // Revenda (pedido 2026-09-07) — mesma ideia de getImages/getSummaries
  // (uma chamada em lote), mas mora em /vehicles no backend (GET
  // /vehicles/company-map), não em /vehicle-metadata. Só devolve entradas
  // pra devices de empresa FILHA da empresa do chamador — vazio na imensa
  // maioria dos casos (empresa sem revenda).
  getResellerCompanyMap: (traccarDeviceIds: (string | number)[]) =>
    getInBatches<VehicleResellerCompanyEntry>("/api/v1/vehicles/company-map", traccarDeviceIds),
  update: (traccarDeviceId: string | number, patch: UpdateVehicleMetadataInput) =>
    gpsDrivePut<VehicleMetadata>(`/api/v1/vehicle-metadata/${traccarDeviceId}`, patch),
  // Comprime aqui (não só no PWA) — mesmo limite de 5 MB do avatar.
  uploadImage: async (traccarDeviceId: string | number, file: File) =>
    gpsDriveUpload<VehicleMetadata>(
      `/api/v1/vehicle-metadata/${traccarDeviceId}/image`,
      await compressImageFile(file),
    ),
  removeImage: (traccarDeviceId: string | number) =>
    gpsDriveDelete<VehicleMetadata>(`/api/v1/vehicle-metadata/${traccarDeviceId}/image`),
  uploadInstallImage: async (traccarDeviceId: string | number, file: File) =>
    gpsDriveUpload<VehicleMetadata>(
      `/api/v1/vehicle-metadata/${traccarDeviceId}/install-image`,
      await compressImageFile(file),
    ),
  removeInstallImage: (traccarDeviceId: string | number) =>
    gpsDriveDelete<VehicleMetadata>(`/api/v1/vehicle-metadata/${traccarDeviceId}/install-image`),
  // Apaga o cadastro inteiro (marca/modelo/instalação/fotos) — chamado
  // junto de vehiclesService.remove() ao excluir um dispositivo, pra não
  // deixar a linha órfã aqui depois que o device já sumiu do Traccar.
  remove: (traccarDeviceId: string | number) =>
    gpsDriveDelete<void>(`/api/v1/vehicle-metadata/${traccarDeviceId}`),
};
